import React, { useState } from 'react';
import PuzzleList from './PuzzleList';
import PuzzleView from './PuzzleView';
import PuzzleCreator from './PuzzleCreator';
import PageCloseButton from '../PageCloseButton';
import type { Cell } from '../../game/types';
import './PuzzlesPage.css';

type PuzzleViewType = 'browse' | 'solve' | 'testlab';

interface PuzzlesPageProps {
  onBack: () => void;
  onStartGameFromSandbox?: (board: (Cell | null)[][], startingPlayer: 'white' | 'black') => void;
  authState?: {
    isAuthenticated: boolean;
    isGuest?: boolean;
    user: {
      id: string;
      username: string;
      email: string;
    } | null;
  };
}

const PuzzlesPage: React.FC<PuzzlesPageProps> = ({ onBack, onStartGameFromSandbox, authState }) => {
  const [currentView, setCurrentView] = useState<PuzzleViewType>('browse');
  const [selectedPuzzleId, setSelectedPuzzleId] = useState<number | null>(null);
  const [browsePuzzleIds, setBrowsePuzzleIds] = useState<number[]>([]);

  const handleViewChange = (view: PuzzleViewType) => {
    setCurrentView(view);
    if (view !== 'solve') {
      setSelectedPuzzleId(null);
    }
  };

  const handlePuzzleSelect = (puzzleId: number, puzzleIds: number[]) => {
    setBrowsePuzzleIds(puzzleIds);
    setSelectedPuzzleId(puzzleId);
    setCurrentView('solve');
  };

  return (
    <div className="puzzles-page">
      <div className="puzzles-header">
        <PageCloseButton onClick={onBack} />
        <h1 className="puzzles-title">
          {currentView === 'testlab' ? 'Puzzles > Test Lab' : 'Puzzles'}
        </h1>
        <div className="puzzles-tabs">
          <button
            className={`puzzles-tab ${currentView === 'browse' ? 'active' : ''}`}
            onClick={() => handleViewChange('browse')}
          >
            Browse
          </button>
          <button
            className={`puzzles-tab ${currentView === 'solve' ? 'active' : ''}`}
            onClick={() => handleViewChange('solve')}
            disabled={!selectedPuzzleId}
          >
            Solve
          </button>
          <button
            className={`puzzles-tab ${currentView === 'testlab' ? 'active' : ''}`}
            onClick={() => handleViewChange('testlab')}
          >
            Test Lab
          </button>
        </div>
      </div>

      <div className="puzzles-content">
        {currentView === 'browse' && (
          <PuzzleList
            onPuzzleSelect={handlePuzzleSelect}
            authState={authState}
          />
        )}

        {currentView === 'solve' && selectedPuzzleId && (
          <PuzzleView
            puzzleId={selectedPuzzleId}
            puzzleIds={browsePuzzleIds}
            onGoToPuzzle={setSelectedPuzzleId}
            onBack={() => handleViewChange('browse')}
          />
        )}
        
        {currentView === 'solve' && !selectedPuzzleId && (
          <div className="puzzles-view-placeholder">
            <p>Please select a puzzle from the Browse tab to solve it.</p>
          </div>
        )}

        {currentView === 'testlab' && (
          <div className="puzzles-sandbox-wrapper">
            <PuzzleCreator
              onPuzzleCreated={() => handleViewChange('browse')}
              onBack={onBack}
              authState={authState}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PuzzlesPage;
